import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Mail, Phone, MapPin, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const services = [
  "EHS Systems & Governance",
  "SAP S/4HANA EHS",
  "Risk & Compliance / ISO Audit",
  "Power BI Dashboards",
  "Incident / MOC Management",
  "Training & Capability",
  "Other",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    service: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and a brief message.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") delete payload[k];
      });
      await axios.post(`${API}/contact`, payload);
      toast.success("Enquiry received. Sabari will respond within 1 business day.");
      setForm({
        name: "",
        email: "",
        phone: "",
        company: "",
        service: "",
        message: "",
      });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        (Array.isArray(err?.response?.data?.detail)
          ? err.response.data.detail[0]?.msg
          : null) ||
        "Submission failed. Please email sgeshwar@gmail.com directly.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-0 border-b border-white/25 px-0 py-3 text-white placeholder:text-white/35 focus:outline-none focus:border-accent-amber transition-colors font-mono text-sm";

  return (
    <section
      data-testid="contact-section"
      id="contact"
      className="bg-bgdark dark-section text-white border-t border-white/10"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left */}
          <div className="lg:col-span-5">
            <p className="mono-label">Engage — Step 01</p>
            <h2 className="font-display text-5xl md:text-6xl lg:text-7xl uppercase tracking-tighter mt-5 leading-[0.9]">
              Let&apos;s walk
              <br />
              your site
              <span className="text-accent-amber">.</span>
            </h2>
            <p className="mt-6 text-white/70 leading-relaxed max-w-md">
              Tell us about your operation. Within one business day Sabari will
              reply with an honest read on scope, fit and next steps. No
              boilerplate proposals.
            </p>

            <div className="mt-12 space-y-6">
              <a
                data-testid="contact-email"
                href="mailto:sabari@sabariconsultant.com"
                className="flex items-start gap-4 group"
              >
                <Mail size={18} className="text-accent-amber mt-0.5 shrink-0" />
                <div>
                  <div className="mono-label">Primary Email</div>
                  <div className="font-display text-lg mt-1 link-underline">
                    sabari@sabariconsultant.com
                  </div>
                  <div className="flex flex-col gap-0.5 mt-1.5">
                    <a
                      data-testid="contact-email-secondary"
                      href="mailto:sabariconsultantsblr@gmail.com"
                      className="font-mono text-xs text-white/50 hover:text-accent-amber transition-colors"
                    >
                      ↳ sabariconsultantsblr@gmail.com
                    </a>
                    <a
                      data-testid="contact-email-tertiary"
                      href="mailto:sgeshwar@gmail.com"
                      className="font-mono text-xs text-white/50 hover:text-accent-amber transition-colors"
                    >
                      ↳ sgeshwar@gmail.com (founder)
                    </a>
                  </div>
                </div>
              </a>
              <a
                data-testid="contact-phone"
                href="tel:+919738509782"
                className="flex items-start gap-4 group"
              >
                <Phone size={18} className="text-accent-amber mt-0.5 shrink-0" />
                <div>
                  <div className="mono-label">Phone / WhatsApp</div>
                  <div className="font-display text-lg mt-1 link-underline">
                    +91 97385 09782
                  </div>
                </div>
              </a>
              <div className="flex items-start gap-4">
                <MapPin size={18} className="text-accent-amber mt-0.5 shrink-0" />
                <div>
                  <div className="mono-label">Office</div>
                  <div className="font-display text-lg mt-1">
                    Bengaluru, Karnataka
                  </div>
                  <div className="text-xs text-white/50 mt-1">
                    M/s Sabari Consultant · Proprietorship · MSME
                  </div>
                </div>
              </div>

              {/* Registration card */}
              <div
                data-testid="contact-registrations"
                className="border border-white/15 mt-6"
              >
                <div className="px-4 py-2 border-b border-white/10 bg-white/[0.03]">
                  <span className="mono-label">Registrations</span>
                </div>
                <dl className="divide-y divide-white/10">
                  <div className="px-4 py-3 flex items-center justify-between gap-4">
                    <dt className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/50">
                      Proprietor
                    </dt>
                    <dd className="font-mono text-sm text-white">
                      Mrs. Sindhu Sabari
                    </dd>
                  </div>
                  <div className="px-4 py-3 flex items-center justify-between gap-4">
                    <dt className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/50">
                      GSTIN
                    </dt>
                    <dd className="font-mono text-sm text-white">
                      29LDPPS1020K1ZY
                    </dd>
                  </div>
                  <div className="px-4 py-3 flex items-center justify-between gap-4">
                    <dt className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/50">
                      UDYAM / MSME
                    </dt>
                    <dd className="font-mono text-sm text-white">
                      KR-03-0707078
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>

          {/* Right — form */}
          <div className="lg:col-span-7">
            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="border border-white/15 p-8 lg:p-10 bg-white/[0.02]"
            >
              <div className="flex items-center justify-between mb-8 pb-4 border-b border-white/10">
                <span className="mono-label">Request — Enquiry Form</span>
                <span className="font-mono text-[11px] text-white/40">
                  Form 01 · v1.0
                </span>
              </div>

              <div className="grid md:grid-cols-2 gap-x-6 gap-y-2">
                <Field label="Full name *">
                  <input
                    data-testid="contact-input-name"
                    type="text"
                    required
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Your name"
                    className={inputCls}
                  />
                </Field>
                <Field label="Email *">
                  <input
                    data-testid="contact-input-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@company.com"
                    className={inputCls}
                  />
                </Field>
                <Field label="Phone">
                  <input
                    data-testid="contact-input-phone"
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="+91 ..."
                    className={inputCls}
                  />
                </Field>
                <Field label="Company / Site">
                  <input
                    data-testid="contact-input-company"
                    type="text"
                    value={form.company}
                    onChange={update("company")}
                    placeholder="Organisation"
                    className={inputCls}
                  />
                </Field>

                <Field label="Service of interest" full>
                  <select
                    data-testid="contact-input-service"
                    value={form.service}
                    onChange={update("service")}
                    className={`${inputCls} appearance-none cursor-pointer`}
                  >
                    <option value="" className="bg-bgdark">
                      Select a service —
                    </option>
                    {services.map((s) => (
                      <option key={s} value={s} className="bg-bgdark">
                        {s}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Project brief *" full>
                  <textarea
                    data-testid="contact-input-message"
                    required
                    rows={5}
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Describe your site, scope or concern..."
                    className={`${inputCls} resize-none`}
                  />
                </Field>
              </div>

              <div className="mt-10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <p className="font-mono text-[11px] text-white/40">
                  Response within 1 business day · Confidential
                </p>
                <button
                  data-testid="contact-submit-button"
                  type="submit"
                  disabled={submitting}
                  className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      Sending <Loader2 size={16} className="animate-spin" />
                    </>
                  ) : (
                    <>
                      Send Enquiry <Send size={16} />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children, full }) {
  return (
    <label className={`block py-3 ${full ? "md:col-span-2" : ""}`}>
      <span className="mono-label">{label}</span>
      {children}
    </label>
  );
}
